// App.js - Complete updated file with hydration mismatch suppression

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import './App.css';
import { LOCAL_CATALOG } from './components/CatalogData';
import Home from './components/Home';
import Products from './components/Products';
import ProductDetail from './components/ProductDetail';
import About from './components/About';
import Certificates from './components/Certificates';
import ShopVideos from './components/ShopVideos';
import Contact from './components/Contact';
import AskSomethingElse from './components/AskSomethingElse';

// Product Detail Page Wrapper Component
const ProductDetailWrapper = ({ 
  setCurrentPage, 
  addToCart, 
  cart, 
  handleSingleInquiryClick 
}) => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [activeProduct, setActiveProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const product = LOCAL_CATALOG.find(item => String(item.id) === String(productId));
    
    if (product) {
      setActiveProduct(product);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [productId]);

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
    setActiveProduct(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <p>Loading product details...</p>
        <button 
          onClick={() => navigate('/products')} 
          style={{ padding: '8px 16px', cursor: 'pointer', marginTop: '16px' }}
        >
          Back to Catalog
        </button>
      </div>
    );
  }

  if (!activeProduct) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Product Not Found</h2>
        <p style={{ color: '#64748b', marginBottom: '24px' }}>
          The product you're looking for doesn't exist or has been removed.
        </p>
        <button 
          onClick={() => navigate('/products')} 
          style={{ 
            padding: '10px 20px', 
            backgroundColor: '#0284c7', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px', 
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          Back to Catalog
        </button>
      </div>
    );
  }

  return (
    <ProductDetail 
      activeProduct={activeProduct} 
      setCurrentPage={setCurrentPage} 
      handleProductClick={handleProductClick}
      addToCart={addToCart} 
      cart={cart} 
      handleSingleInquiryClick={handleSingleInquiryClick} 
    />
  );
};

// Helmet wrapper component with suppressHydrationWarning
const HelmetWrapper = ({ children }) => {
  return (
    <div suppressHydrationWarning>
      {children}
    </div>
  );
};

// Main App Content Component (with routing hooks)
const AppContent = () => {
  const [currentPage, setCurrentPage] = useState('home'); 
  const [searchQuery, setSearchQuery] = useState('');
  const [activeProduct, setActiveProduct] = useState(null);

  // 💡 FRONTEND FILTER STATE MANAGEMENT
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Cart & Modal Management States
  const [cart, setCart] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('bulk'); 
  const [selectedAsset, setSelectedAsset] = useState(null); 
  
  // Mobile Responsiveness States - FIXED: Default to false for SSR
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Scroll Tracking State for Dynamic Header Transition
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Navbar height state
  const [navbarHeight, setNavbarHeight] = useState(0);

  // Global Newsletter State
  const [emailInput, setEmailInput] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  // 🛒 CART ANIMATION STATES
  const [flyingItem, setFlyingItem] = useState(null);
  const [cartBounce, setCartBounce] = useState(false);
  const cartButtonRef = useRef(null);
  const cartButtonRef2 = useRef(null);

  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    pincode: '',
    city: '',
    notes: ''
  });

  const navigate = useNavigate();
  const location = useLocation();

  // FIXED: Track window resizing and scroll behavior - client only
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 968);
    };
    
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Set initial values
    handleResize();
    handleScroll();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // FIXED: Measure navbar height - client only
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;
    
    const measureNavbar = () => {
      const navbar = document.querySelector('.main-header');
      if (navbar) {
        setNavbarHeight(navbar.offsetHeight);
      }
    };
    
    measureNavbar();
    window.addEventListener('resize', measureNavbar);
    
    return () => {
      window.removeEventListener('resize', measureNavbar);
    };
  }, [isMobile, isScrolled, location.pathname]);

  // Force scroll reset on route change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'instant' 
        });
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [location.pathname, activeProduct]);

  // 🛒 CART ANIMATION - Handle fly effect
  const animateAddToCart = (product, sourceElement) => {
    if (!sourceElement || typeof window === 'undefined') return;
    
    const sourceRect = sourceElement.getBoundingClientRect();
    const cartRect = cartButtonRef.current?.getBoundingClientRect();
    
    if (!cartRect) {
      addToCartDirect(product);
      return;
    }
    
    const flyer = document.createElement('div');
    flyer.style.cssText = `
      position: fixed;
      width: ${sourceRect.width}px;
      height: ${sourceRect.height}px;
      left: ${sourceRect.left}px;
      top: ${sourceRect.top}px;
      background: ${sourceRect.width > 100 ? '#ffffff' : 'transparent'};
      border-radius: 8px;
      pointer-events: none;
      z-index: 9999;
      transition: all 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
      box-shadow: 0 4px 20px rgba(0,0,0,0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
    `;
    
    if (sourceRect.width > 100) {
      const img = product.image || product.imageUrl;
      flyer.innerHTML = `
        <div style="
          width: 100%;
          height: 100%;
          background: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          padding: 4px;
        ">
          ${img ? `<img src="${img}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 4px;" />` : '📦'}
          <span style="position: absolute; bottom: 2px; right: 4px; font-size: 10px; background: #0284c7; color: white; padding: 1px 6px; border-radius: 10px; font-weight: 700;">+1</span>
        </div>
      `;
    } else {
      flyer.innerHTML = `
        <div style="
          width: 100%;
          height: 100%;
          background: #0284c7;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
        ">
          ✦
        </div>
      `;
    }
    
    document.body.appendChild(flyer);
    
    requestAnimationFrame(() => {
      const targetX = cartRect.left + cartRect.width / 2 - sourceRect.width / 2;
      const targetY = cartRect.top + cartRect.height / 2 - sourceRect.height / 2;
      
      flyer.style.left = `${targetX}px`;
      flyer.style.top = `${targetY}px`;
      flyer.style.width = `${Math.min(sourceRect.width * 0.3, 40)}px`;
      flyer.style.height = `${Math.min(sourceRect.height * 0.3, 40)}px`;
      flyer.style.borderRadius = '50%';
      flyer.style.opacity = '0.8';
      flyer.style.transform = 'scale(0.5)';
    });
    
    setTimeout(() => {
      flyer.remove();
      addToCartDirect(product);
      setCartBounce(true);
      setTimeout(() => setCartBounce(false), 500);
    }, 750);
  };

  const addToCartDirect = (asset) => {
    if (!cart.some(item => item.id === asset.id)) {
      setCart(prev => [...prev, asset]);
    }
  };

  const addToCart = (asset, event) => {
    if (cart.some(item => item.id === asset.id)) {
      setCartBounce(true);
      setTimeout(() => setCartBounce(false), 500);
      return;
    }
    
    let sourceElement = null;
    if (event && event.currentTarget) {
      sourceElement = event.currentTarget;
    } else if (event && event.target) {
      sourceElement = event.target.closest('button') || event.target;
    }
    
    if (sourceElement) {
      animateAddToCart(asset, sourceElement);
    } else {
      addToCartDirect(asset);
      setCartBounce(true);
      setTimeout(() => setCartBounce(false), 500);
    }
  };

  const removeFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const handleSingleInquiryClick = (asset) => {
    setSelectedAsset(asset);
    setModalType('single');
    setIsModalOpen(true);
  };

  const invokeRfqSession = () => {
    if (cart.length === 0) {
      alert("Your inquiry list is empty!");
      return;
    }
    setModalType('bulk');
    setIsModalOpen(true);
  };

  const navigateTo = (page) => {
    const routeMap = {
      'home': '/',
      'products': '/products',
      'product-detail': '/product-detail',
      'about': '/about',
      'certificates': '/certificates',
      'videos': '/videos',
      'contact': '/contact',
      'custom-request': '/custom-request'
    };
    navigate(routeMap[page] || '/');
    setIsMenuOpen(false);
  };

  const BUSINESS_EMAIL = "shabaaztradingco04@gmail.com"; 
  const WHATSAPP_NUMBER = "919016235501"; 
  const SECONDARY_NUMBER = "917861862566";
  const INSTAGRAM_URL = "https://www.instagram.com/shabaaztrading/";
  const YOUTUBE_URL = "https://www.youtube.com/channel/UC92IGBofPfsgbu86ERVlAVQ";

  const openWhatsApp = (phoneNumber, message) => {
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getAbsoluteImageUrl = (imagePath) => {
    if (!imagePath) return "No image link available";
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${window.location.origin}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  const handleSubscribeSubmit = () => {
    if (!emailInput) {
      alert('Please enter a valid email address.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      alert('Please enter a valid email address (e.g., name@example.com).');
      return;
    }

    setIsSubscribing(true);

    const subject = encodeURIComponent(`Newsletter Subscription - ${emailInput}`);
    const body = encodeURIComponent(
      `Dear Shabaaz Trading Co.,\n\n` +
      `A new user has subscribed to your newsletter:\n\n` +
      `📧 Email: ${emailInput}\n` +
      `📅 Date: ${new Date().toLocaleString()}\n` +
      `🌐 IP Address: ${window.location.hostname}\n\n` +
      `Please add this email to your mailing list.\n\n` +
      `Best regards,\n` +
      `System Notification`
    );

    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${BUSINESS_EMAIL}&su=${subject}&body=${body}`, '_blank');
    
    setTimeout(() => {
      alert(`Thank you for subscribing! We'll send updates to ${emailInput}`);
      setEmailInput('');
      setIsSubscribing(false);
    }, 500);
  };

  const buildSingleEmailBody = () => {
    const imgUrl = getAbsoluteImageUrl(selectedAsset?.image || selectedAsset?.imageUrl);
    return (
      `Dear Shabaaz Trading Co. Export Desk,\n\n` +
      `I am interested in requesting an instant quote for the following product:\n` +
      `-----------------------------------------\n` +
      `Product Name: ${selectedAsset?.name}\n` +
      `Category: ${selectedAsset?.category || 'General'}\n` +
      `Asset Reference ID: ${selectedAsset?.id}\n` +
      `Product Image Link: ${imgUrl}\n` +
      `-----------------------------------------\n\n` +
      `CUSTOMER CONTACT DETAILS:\n` +
      `• Customer Name: ${formData.customerName}\n` +
      `• Phone Number: ${formData.phone}\n` +
      `• Pincode / ZIP: ${formData.pincode}\n` +
      `• City: ${formData.city}\n\n` +
      `EXTRA NOTES / SPECIAL REQUIREMENTS:\n` +
      `${formData.notes || "None provided."}\n\n` +
      `Regards,\n${formData.customerName}`
    );
  };

  const buildBulkEmailBody = () => {
    let listText = "";
    cart.forEach((item, i) => {
      const imgUrl = getAbsoluteImageUrl(item.image || item.imageUrl);
      listText += `${i + 1}. Name: ${item.name} | Category: ${item.category || 'General'} | Ref ID: ${item.id}\n   Image Link: ${imgUrl}\n\n`;
    });
    return (
      `Dear Shabaaz Trading Co. Export Desk,\n\n` +
      `PRODUCTS\n` +
      `-----------------------------------------------------------------------\n` +
      `${listText}` +
      `-----------------------------------------------------------------------\n\n` +
      `CUSTOMER CONTACT DETAILS:\n` +
      `• Customer Name: ${formData.customerName}\n` +
      `• Phone Number: ${formData.phone}\n` +
      `• Pincode / ZIP: ${formData.pincode}\n` +
      `• City: ${formData.city}\n\n` +
      `EXTRA NOTES / SPECIAL REQUIREMENTS:\n` +
      `${formData.notes || "None provided."}\n\n` +
      `Regards,\n${formData.customerName}`
    );
  };

  const buildSingleWhatsAppMessage = () => {
    const imgUrl = getAbsoluteImageUrl(selectedAsset?.image || selectedAsset?.imageUrl);
    return (
      `*INSTANT ORDER INQUIRY - SHABAAZ TRADING CO.*\n\n` +
      `*Product Details:*\n` +
      `• Name: ${selectedAsset?.name}\n` +
      `• Category: ${selectedAsset?.category || 'General'}\n` +
      `• Image Link: ${imgUrl}\n\n` +
      `*Customer Details:*\n` +
      `• Name: ${formData.customerName}\n` +
      `• Phone: ${formData.phone}\n` +
      `• Pincode: ${formData.pincode}\n` +
      `• City: ${formData.city}\n\n` +
      `*Notes:* ${formData.notes || "None"}`
    );
  };

  const buildBulkWhatsAppMessage = () => {
    let listText = "";
    cart.forEach((item, i) => {
      const imgUrl = getAbsoluteImageUrl(item.image || item.imageUrl);
      listText += `${i + 1}. ${item.name} (ID: ${item.id})\n   Link: ${imgUrl}\n`;
    });
    return (
      `*BULK ORDER INQUIRY - SHABAAZ TRADING CO.*\n\n` +
      `*Customer Details:*\n` +
      `• Name: ${formData.customerName}\n` +
      `• Phone: ${formData.phone}\n` +
      `• Pincode: ${formData.pincode}\n` +
      `• City: ${formData.city}\n\n` +
      `*Requested Products (${cart.length}):*\n${listText}\n` +
      `*Notes:* ${formData.notes || "None"}`
    );
  };

  const handleWhatsAppSubmit = () => {
    if (!formData.customerName || !formData.phone || !formData.pincode || !formData.city) {
      alert("Please fill out all required fields.");
      return;
    }
    const text = modalType === 'single' ? buildSingleWhatsAppMessage() : buildBulkWhatsAppMessage();
    openWhatsApp(WHATSAPP_NUMBER, text);
    closeModalAndReset();
  };

  const handleSecondaryWhatsApp = () => {
    const message = `*Hello Shabaaz Trading Co.*\n\nI would like to get more information about your products and services.\n\nRegards,`;
    openWhatsApp(SECONDARY_NUMBER, message);
  };

  const handleNativeMailSubmit = () => {
    if (!formData.customerName || !formData.phone || !formData.pincode || !formData.city) {
      alert("Please fill out all required fields.");
      return;
    }
    const subject = encodeURIComponent(modalType === 'single' ? `Instant Inquiry: ${selectedAsset?.name}` : `Bulk Inquiry Request (${cart.length} Items)`);
    const body = encodeURIComponent(modalType === 'single' ? buildSingleEmailBody() : buildBulkEmailBody());
    window.open(`mailto:${BUSINESS_EMAIL}?subject=${subject}&body=${body}`, '_self');
    closeModalAndReset();
  };

  const handleWebGmailSubmit = () => {
    if (!formData.customerName || !formData.phone || !formData.pincode || !formData.city) {
      alert("Please fill out all required fields.");
      return;
    }
    const subject = encodeURIComponent(modalType === 'single' ? `Instant Inquiry: ${selectedAsset?.name}` : `Bulk Inquiry Request (${cart.length}Items)`);
    const body = encodeURIComponent(modalType === 'single' ? buildSingleEmailBody() : buildBulkEmailBody());
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${BUSINESS_EMAIL}&su=${subject}&body=${body}`, '_blank');
    closeModalAndReset();
  };

  const closeModalAndReset = () => {
    setIsModalOpen(false);
    if (modalType === 'bulk') setCart([]);
    setSelectedAsset(null);
    setFormData({ customerName: '', phone: '', pincode: '', city: '', notes: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`);
  };

  const filteredAssets = (LOCAL_CATALOG || []).filter(asset => {
    const productName = asset?.name?.toLowerCase() || '';
    const productCategory = asset?.category?.toLowerCase() || '';
    const cleanSearch = searchQuery.toLowerCase();
    
    const matchesSearch = productName.includes(cleanSearch) || productCategory.includes(cleanSearch);
    const matchesCategory = selectedCategory === 'All' || asset?.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getNavButtonStyle = (pageId) => {
    const currentRoute = location.pathname === '/' ? 'home' : location.pathname.substring(1);
    const isActive = currentRoute === pageId || (pageId === 'home' && location.pathname === '/');
    return {
      width: isMobile ? '100%' : 'auto',
      textAlign: isMobile ? 'left' : 'center',
      background: isActive ? '#0f172a' : 'transparent',
      color: isActive ? '#ffffff' : '#475569',
      border: 'none',
      padding: isMobile ? '12px 18px' : '6px 12px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontWeight: '700',
      fontSize: '0.8rem',
      letterSpacing: '-0.01em',
      transition: 'all 0.15s ease',
      boxSizing: 'border-box',
      whiteSpace: 'nowrap'
    };
  };

  const BasketButton = ({ refProp, isMobileVersion }) => (
    <button 
      ref={refProp}
      onClick={invokeRfqSession}
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '6px', 
        padding: isMobileVersion ? '6px 12px' : '6px 12px', 
        backgroundColor: cart.length > 0 ? '#0284c7' : '#0f172a', 
        color: '#ffffff', 
        border: 'none', 
        borderRadius: '8px', 
        cursor: 'pointer', 
        fontWeight: '700', 
        fontSize: isMobileVersion ? '0.75rem' : '0.8rem', 
        whiteSpace: 'nowrap', 
        marginLeft: 'auto',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.3s ease',
        transform: cartBounce ? 'scale(1.15)' : 'scale(1)',
        position: 'relative'
      }}
    >
      🛒 Basket 
      <span style={{ 
        background: cart.length > 0 ? '#ffffff' : 'rgba(255,255,255,0.2)', 
        color: cart.length > 0 ? '#0284c7' : '#ffffff',
        padding: '1px 8px', 
        borderRadius: '20px',
        fontSize: isMobileVersion ? '0.65rem' : '0.7rem',
        fontWeight: '800',
        minWidth: '20px',
        textAlign: 'center'
      }}>
        {cart.length}
      </span>
      {cart.length > 0 && (
        <span style={{
          position: 'absolute',
          top: '-4px',
          right: '-4px',
          width: '10px',
          height: '10px',
          background: '#22c55e',
          borderRadius: '50%',
          animation: 'pulse-dot 1.5s ease-in-out infinite'
        }} />
      )}
    </button>
  );

  return (
    <>
      {/* SEO META TAGS - Wrapped in div with suppressHydrationWarning */}
      <div suppressHydrationWarning>
        <Helmet>
          <title>Shabaaz Trading Co. - Industrial Pumps, Gear Motors, Chain Hoists, Marine Equipment Supplier</title>
          <meta name="description" content="Shabaaz Trading Co. - Leading supplier of Submersible Pumps, Centrifugal Pumps, Gear Motors, Waste Water Pumps, Ship Machinery, Marine Equipment, Chain Hoists, Butterfly Valves, Blowers, SS Pressure Pumps. GST: 24ABUPL6740K1Z4. Call +91 9016235501." />
          <meta name="keywords" content="Submersible Pump, Centrifugal Pump, Gear Motors, Waste Water Pump, Ship Machinery, Marine Equipment, Chain Hoist, Butterfly Valve, Blower, SS Pressure Pump, Industrial Pumps" />
          <meta name="author" content="Shabaaz Trading Co." />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <link rel="canonical" href="https://shabaaztradingco.com/" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://shabaaztradingco.com/" />
          <meta property="og:title" content="Shabaaz Trading Co. - Industrial Pumps, Gear Motors, Chain Hoists, Marine Equipment Supplier" />
          <meta property="og:description" content="Shabaaz Trading Co. - Leading supplier of Submersible Pumps, Centrifugal Pumps, Gear Motors, Waste Water Pumps, Ship Machinery, Marine Equipment, Chain Hoists, Butterfly Valves, Blowers, SS Pressure Pumps." />
          <meta property="og:image" content="https://shabaaztradingco.com/logo.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Shabaaz Trading Co. - Industrial Pumps, Gear Motors, Chain Hoists, Marine Equipment Supplier" />
          <meta name="twitter:description" content="Trusted supplier of Submersible Pumps, Centrifugal Pumps, Gear Motors, Marine Equipment, Chain Hoists, Butterfly Valves, Blowers. RFQ response under 2 hours." />
          <meta name="geo.region" content="IN-GJ" />
          <meta name="geo.placename" content="Porbandar, Gujarat, India" />
          <meta name="geo.position" content="21.6417;69.6293" />
          <meta name="ICBM" content="21.6417, 69.6293" />
          
          {/* UPDATED SCHEMA MARKUP - FIXED FOR GOOGLE SEARCH CONSOLE */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "Organization",
                "name": "Shabaaz Trading Co.",
                "url": "https://shabaaztradingco.com",
                "logo": "https://shabaaztradingco.com/logo.png",
                "description": "Leading supplier of Submersible Pumps, Centrifugal Pumps, Gear Motors, Waste Water Pumps, Ship Machinery, Marine Equipment, Chain Hoists, Butterfly Valves, Blowers, SS Pressure Pumps",
                "email": "shabaaztradingco04@gmail.com",
                "telephone": "+919016235501",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Bhavnagar",
                  "addressRegion": "Gujarat",
                  "addressCountry": "IN"
                },
                "sameAs": [
                  "https://www.instagram.com/shabaaztrading/",
                  "https://www.youtube.com/channel/UC92IGBofPfsgbu86ERVlAVQ"
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+919016235501",
                  "contactType": "customer service",
                  "availableLanguage": ["English", "Hindi"]
                }
              }
            `}
          </script>
          
          {/* PRODUCT SCHEMA - FIXED WITH ALL REQUIRED FIELDS */}
          <script type="application/ld+json">
            {`
              {
                "@context": "https://schema.org",
                "@type": "ItemList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "item": {
                      "@type": "Product",
                      "name": "Blower",
                      "image": "https://shabaaztradingco.com/images/blower.jpg",
                      "description": "High-quality industrial blower for ventilation, dust collection, and material handling applications. Available in various sizes and capacities.",
                      "sku": "BL-001",
                      "brand": {
                        "@type": "Brand",
                        "name": "Shabaaz Trading Co."
                      },
                      "offers": {
                        "@type": "Offer",
                        "priceCurrency": "INR",
                        "price": "0",
                        "availability": "https://schema.org/InStock",
                        "url": "https://shabaaztradingco.com/product/blower"
                      }
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "item": {
                      "@type": "Product",
                      "name": "Butterfly Valve",
                      "image": "https://shabaaztradingco.com/images/butterfly-valve.jpg",
                      "description": "Industrial butterfly valve for flow control in pipelines. Suitable for water, oil, gas, and chemical applications. Durable and corrosion-resistant.",
                      "sku": "BV-001",
                      "brand": {
                        "@type": "Brand",
                        "name": "Shabaaz Trading Co."
                      },
                      "offers": {
                        "@type": "Offer",
                        "priceCurrency": "INR",
                        "price": "0",
                        "availability": "https://schema.org/InStock",
                        "url": "https://shabaaztradingco.com/product/butterfly-valve"
                      }
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "item": {
                      "@type": "Product",
                      "name": "Chain Block & Electric Chain Hoist",
                      "image": "https://shabaaztradingco.com/images/chain-hoist.jpg",
                      "description": "Heavy-duty chain block and electric chain hoist for lifting and material handling. Available in manual and electric models with various load capacities.",
                      "sku": "CH-001",
                      "brand": {
                        "@type": "Brand",
                        "name": "Shabaaz Trading Co."
                      },
                      "offers": {
                        "@type": "Offer",
                        "priceCurrency": "INR",
                        "price": "0",
                        "availability": "https://schema.org/InStock",
                        "url": "https://shabaaztradingco.com/product/chain-hoist"
                      }
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 4,
                    "item": {
                      "@type": "Product",
                      "name": "Gear Motors",
                      "image": "https://shabaaztradingco.com/images/gear-motor.jpg",
                      "description": "Cyclo, Cyclone, Reduction, and Aliger gear motors for industrial applications. High efficiency, low maintenance, and reliable performance.",
                      "sku": "GM-001",
                      "brand": {
                        "@type": "Brand",
                        "name": "Shabaaz Trading Co."
                      },
                      "offers": {
                        "@type": "Offer",
                        "priceCurrency": "INR",
                        "price": "0",
                        "availability": "https://schema.org/InStock",
                        "url": "https://shabaaztradingco.com/product/gear-motors"
                      }
                    }
                  },
                  {
                    "@type": "ListItem",
                    "position": 5,
                    "item": {
                      "@type": "Product",
                      "name": "Marine Pumps",
                      "image": "https://shabaaztradingco.com/images/marine-pump.jpg",
                      "description": "Submersible, Centrifugal, Waste Water, and SS Pressure pumps for marine and industrial applications. Built for durability and high performance.",
                      "sku": "MP-001",
                      "brand": {
                        "@type": "Brand",
                        "name": "Shabaaz Trading Co."
                      },
                      "offers": {
                        "@type": "Offer",
                        "priceCurrency": "INR",
                        "price": "0",
                        "availability": "https://schema.org/InStock",
                        "url": "https://shabaaztradingco.com/product/marine-pumps"
                      }
                    }
                  }
                ]
              }
            `}
          </script>
          
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        </Helmet>
      </div>

      {/* MAIN HEADER NAVIGATION */}
      <nav className="main-header" style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0,
        right: 0,
        zIndex: 1000,
        display: 'flex', 
        flexDirection: 'column',
        padding: isMobile ? '10px 20px' : '12px 48px', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(20px)', 
        borderBottom: '1px solid #e2e8f0', 
        boxShadow: isScrolled ? '0 8px 20px -4px rgba(0, 0, 0, 0.12)' : '0 2px 10px rgba(0, 0, 0, 0.05)',
        transition: 'box-shadow 0.3s ease, background 0.3s ease',
        boxSizing: 'border-box'
      }}>
        
        {/* ROW 1: BRAND LOGO + CONTROL ALIGNMENTS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          
          {/* Logo */}
          <div className="brand-block" onClick={() => navigateTo('home')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', height: isMobile ? '36px' : '44px' }}>
            <img 
              src="/images/logo.png" 
              alt="Shabaaz Trading Co. Logo" 
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} 
            />
          </div>

          {/* Desktop Control Panel */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', height: '40px', position: 'relative', flex: 1, justifyContent: 'flex-end' }}>
              
              {/* Desktop Contact View */}
              <div style={{ 
                display: 'flex', 
                gap: '20px', 
                alignItems: 'center',
                opacity: isScrolled ? 0 : 1,
                pointerEvents: isScrolled ? 'none' : 'auto',
                transform: isScrolled ? 'translateY(-5px)' : 'translateY(0)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                position: 'absolute',
                right: 0
              }}>
                <a href={`tel:${WHATSAPP_NUMBER}`} style={{ textDecoration: 'none', color: '#334155', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  📞 <span style={{ color: '#0f172a' }}>+{WHATSAPP_NUMBER}</span>
                </a>
                <button onClick={handleSecondaryWhatsApp} style={{ textDecoration: 'none', color: '#334155', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', cursor: 'pointer' }}>
                  📞 <span style={{ color: '#0f172a' }}>+91 7861862566</span>
                </button>
                <a href={`mailto:${BUSINESS_EMAIL}`} style={{ textDecoration: 'none', color: '#334155', fontWeight: '600', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  ✉️ <span style={{ color: '#0f172a' }}>{BUSINESS_EMAIL}</span>
                </a>
              </div>

              {/* Desktop Navigation Row - SCROLL-UP ROW */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: isScrolled ? 1 : 0,
                pointerEvents: isScrolled ? 'auto' : 'none',
                transform: isScrolled ? 'translateY(0)' : 'translateY(5px)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
                position: 'absolute',
                right: 0
              }}>
                <button onClick={() => navigateTo('home')} style={getNavButtonStyle('home')}>Home</button>
                <button onClick={() => navigateTo('products')} style={getNavButtonStyle('products')}>Products</button>
                <button onClick={() => navigateTo('about')} style={getNavButtonStyle('about')}>About Us</button>
                <button onClick={() => navigateTo('certificates')} style={getNavButtonStyle('certificates')}>Certificates</button>
                <button onClick={() => navigateTo('videos')} style={getNavButtonStyle('videos')}>Shop Videos</button>
                <button onClick={() => navigateTo('contact')} style={getNavButtonStyle('contact')}>Contact Us</button>
                <button onClick={() => navigateTo('custom-request')} style={{
                  ...getNavButtonStyle('custom-request'),
                  color: location.pathname === '/custom-request' ? '#ffffff' : '#0284c7',
                  border: location.pathname === '/custom-request' ? 'none' : '1px dashed rgba(2, 132, 199, 0.5)'
                }}>Ask Custom Sourcing</button>

                <BasketButton refProp={cartButtonRef} isMobileVersion={false} />
              </div>
            </div>
          )}
          
          {/* Mobile Action Triggers */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                ref={cartButtonRef}
                onClick={invokeRfqSession}
                style={{ 
                  padding: '6px 12px', 
                  backgroundColor: cart.length > 0 ? '#0284c7' : '#64748b', 
                  color: '#ffffff', 
                  border: 'none', 
                  borderRadius: '30px', 
                  cursor: 'pointer', 
                  fontWeight: '700', 
                  fontSize: '0.75rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  transform: cartBounce ? 'scale(1.15)' : 'scale(1)',
                  position: 'relative'
                }}
              >
                🛒 <span>({cart.length})</span>
                {cart.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-2px',
                    width: '8px',
                    height: '8px',
                    background: '#22c55e',
                    borderRadius: '50%',
                    animation: 'pulse-dot 1.5s ease-in-out infinite'
                  }} />
                )}
              </button>
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isMenuOpen ? '✕' : '☰'}
              </button>
            </div>
          )}
        </div>
        
        {/* Mobile Contact Layer - PHONE & EMAIL IN ONE ROW */}
        {isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '8px',
            opacity: isScrolled ? 0 : 1,
            pointerEvents: isScrolled ? 'none' : 'auto',
            transform: isScrolled ? 'translateY(-3px)' : 'translateY(0)',
            transition: 'opacity 0.15s ease, transform 0.15s ease',
            marginTop: '4px',
            paddingRight: '4px',
            flexWrap: 'nowrap',
            width: '100%',
            overflow: 'hidden'
          }}>
            <a href={`tel:${WHATSAPP_NUMBER}`} style={{ 
              textDecoration: 'none', 
              color: '#475569', 
              fontWeight: '500', 
              fontSize: '0.6rem', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '2px',
              whiteSpace: 'nowrap',
              padding: '2px 5px',
              background: '#f1f5f9',
              borderRadius: '4px',
              flexShrink: 0
            }}>
              📞 <span style={{ fontSize: '0.55rem' }}>+{WHATSAPP_NUMBER}</span>
            </a>
            <a href={`mailto:${BUSINESS_EMAIL}`} style={{ 
              textDecoration: 'none', 
              color: '#475569', 
              fontWeight: '500', 
              fontSize: '0.6rem', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '2px',
              whiteSpace: 'nowrap',
              padding: '2px 5px',
              background: '#f1f5f9',
              borderRadius: '4px',
              flexShrink: 0
            }}>
              ✉️ <span style={{ fontSize: '0.55rem' }}>{BUSINESS_EMAIL}</span>
            </a>
          </div>
        )}

        {/* Desktop Primary Stable Link Trackway - STATIC BOTTOM ROW */}
        {!isMobile && (
          <div style={{ 
            opacity: isScrolled ? 0 : 1,
            pointerEvents: isScrolled ? 'none' : 'auto',
            transform: isScrolled ? 'translateY(-5px)' : 'translateY(0)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            paddingTop: '8px',
            marginTop: '8px'
          }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flex: 1 }}>
              <button onClick={() => navigateTo('home')} style={getNavButtonStyle('home')}>Home</button>
              <button onClick={() => navigateTo('products')} style={getNavButtonStyle('products')}>Products</button>
              <button onClick={() => navigateTo('about')} style={getNavButtonStyle('about')}>About Us</button>
              <button onClick={() => navigateTo('certificates')} style={getNavButtonStyle('certificates')}>Certificates</button>
              <button onClick={() => navigateTo('videos')} style={getNavButtonStyle('videos')}>Shop Videos</button>
              <button onClick={() => navigateTo('contact')} style={getNavButtonStyle('contact')}>Contact Us</button>
              <button onClick={() => navigateTo('custom-request')} style={{
                ...getNavButtonStyle('custom-request'),
                color: location.pathname === '/custom-request' ? '#ffffff' : '#0284c7',
                border: location.pathname === '/custom-request' ? 'none' : '1px dashed rgba(2, 132, 199, 0.5)'
              }}>Ask Custom Sourcing</button>
            </div>

            <BasketButton refProp={cartButtonRef2} isMobileVersion={false} />
          </div>
        )}

        {/* Mobile Navigation Drawer */}
        {isMobile && isMenuOpen && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <button onClick={() => navigateTo('home')} style={getNavButtonStyle('home')}>Home</button>
            <button onClick={() => navigateTo('products')} style={getNavButtonStyle('products')}>Products</button>
            <button onClick={() => navigateTo('about')} style={getNavButtonStyle('about')}>About Us</button>
            <button onClick={() => navigateTo('certificates')} style={getNavButtonStyle('certificates')}>Certificates</button>
            <button onClick={() => navigateTo('videos')} style={getNavButtonStyle('videos')}>Shop Videos</button>
            <button onClick={() => navigateTo('contact')} style={getNavButtonStyle('contact')}>Contact Us</button>
            <button onClick={() => navigateTo('custom-request')} style={{
              ...getNavButtonStyle('custom-request'),
              color: location.pathname === '/custom-request' ? '#ffffff' : '#0284c7',
              border: location.pathname === '/custom-request' ? 'none' : '1px dashed rgba(2, 132, 199, 0.5)'
            }}>Ask Custom Sourcing</button>
            <button onClick={handleSecondaryWhatsApp} style={{
              ...getNavButtonStyle('custom-request'),
              background: '#25D366',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center'
            }}>
              💬 WhatsApp on +91 7861862566
            </button>
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" style={{
              ...getNavButtonStyle('custom-request'),
              background: '#FF0000',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center',
              textDecoration: 'none'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.376.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.376-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Subscribe on YouTube
            </a>
          </div>
        )}
      </nav>

      {/* MAIN CONTENT WRAPPER */}
      <div className="App" style={{ 
        fontFamily: '"Inter", -apple-system, sans-serif', 
        backgroundColor: '#fafbfe', 
        color: '#0f172a', 
        minHeight: '100vh', 
        position: 'relative', 
        boxSizing: 'border-box', 
        display: 'flex', 
        flexDirection: 'column', 
        width: '100%', 
        overflowX: 'hidden',
        paddingTop: `${navbarHeight}px`
      }}>
        
        {/* RENDER DYNAMIC PAGES */}
        <Routes>
          <Route path="/" element={
            <Home setCurrentPage={navigateTo} handleProductClick={handleProductClick} addToCart={addToCart} cart={cart} handleSingleInquiryClick={handleSingleInquiryClick} />
          } />
          
          <Route path="/products" element={
            <Products 
              filteredAssets={filteredAssets} 
              searchQuery={searchQuery} 
              setSearchQuery={setSearchQuery} 
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              handleProductClick={handleProductClick} 
              addToCart={addToCart} 
              cart={cart} 
              handleSingleInquiryClick={handleSingleInquiryClick} 
              setCurrentPage={navigateTo} 
            />
          } />
          
          <Route path="/product/:productId" element={
            <ProductDetailWrapper 
              setCurrentPage={navigateTo}
              addToCart={addToCart}
              cart={cart}
              handleSingleInquiryClick={handleSingleInquiryClick}
            />
          } />
          
          <Route path="/product-detail" element={
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <h2>Invalid Product Link</h2>
              <p>Please use the product links from the catalog.</p>
              <button onClick={() => navigateTo('products')} style={{ padding: '10px 20px', cursor: 'pointer' }}>Back to Products</button>
            </div>
          } />
          
          <Route path="/about" element={<About />} />
          <Route path="/certificates" element={<Certificates setCurrentPage={navigateTo} />} />
          <Route path="/videos" element={<ShopVideos setCurrentPage={navigateTo} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/custom-request" element={<AskSomethingElse />} />
        </Routes>

        {/* FOOTER */}
        <footer style={{ 
          backgroundColor: '#0f172a', 
          color: '#94a3b8', 
          padding: '60px 20px 30px 20px',
          width: '100%',
          boxSizing: 'border-box',
          marginTop: 'auto'
        }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '40px', paddingBottom: '40px', borderBottom: '1px solid #334155' }}>
            
            <div style={{ flex: '2', minWidth: '250px' }}>
              <h3 style={{ color: '#ffffff', margin: '0 0 15px 0', fontSize: '1.4rem', fontWeight: '800' }}>SHABAAZ TRADING CO.</h3>
              <p style={{ fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                Leading supplier of Submersible Pumps, Centrifugal Pumps, Gear Motors, Waste Water Pumps, Ship Machinery, Marine Equipment, Chain Hoists, Butterfly Valves, Blowers, SS Pressure Pumps.
              </p>
            </div>

            <div style={{ flex: '1.2', minWidth: '180px' }}>
              <h4 style={{ color: '#ffffff', margin: '0 0 15px 0', fontSize: '1rem', fontWeight: '600' }}>Navigation</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.9rem' }}>
                <li><span onClick={() => navigateTo('home')} style={{ cursor: 'pointer' }}>Home</span></li>
                <li><span onClick={() => navigateTo('products')} style={{ cursor: 'pointer' }}>Products Portfolio</span></li>
                <li><span onClick={() => navigateTo('about')} style={{ cursor: 'pointer' }}>About Us</span></li>
                <li><span onClick={() => navigateTo('certificates')} style={{ cursor: 'pointer' }}>Certificates</span></li>
                <li><span onClick={() => navigateTo('contact')} style={{ cursor: 'pointer' }}>Contact Us</span></li>
              </ul>
            </div>

            <div style={{ flex: '1.5', minWidth: '250px' }}>
              <h4 style={{ color: '#ffffff', margin: '0 0 15px 0', fontSize: '1rem', fontWeight: '600' }}>Stay Connected</h4>
              <p style={{ fontSize: '0.85rem', marginBottom: '15px' }}>Subscribe to get alerts on new structural inventory items.</p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="email" 
                  placeholder="Your corporate email" 
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  style={{ padding: '10px', borderRadius: '6px', border: '1px solid #334155', backgroundColor: '#1e293b', color: '#fff', fontSize: '0.85rem', flex: '1', outline: 'none' }} 
                />
                <button 
                  style={{ padding: '10px 15px', backgroundColor: '#eeff00', color: '#0f172a', border: 'none', borderRadius: '6px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }} 
                  onClick={handleSubscribeSubmit}
                  disabled={isSubscribing}
                >
                  {isSubscribing ? 'Sending...' : 'Join'}
                </button>
              </div>
              
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button 
                  onClick={handleSecondaryWhatsApp}
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    backgroundColor: '#25D366',
                    color: '#ffffff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '30px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, background 0.2s ease',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.032 2.001c-5.514 0-9.999 4.485-10 9.998 0 1.764.459 3.498 1.333 5.016l-1.416 4.982 5.119-1.388c1.466.801 3.112 1.222 4.79 1.222h.004c5.514 0 9.999-4.486 10-9.999 0-5.514-4.486-10-10-10zm0 18.4c-1.5 0-2.976-.402-4.274-1.162l-.307-.183-3.038.824.815-2.963-.199-.319c-.841-1.33-1.285-2.86-1.285-4.42 0-4.6 3.742-8.341 8.344-8.341 4.6 0 8.341 3.742 8.341 8.342 0 4.6-3.741 8.342-8.341 8.342zm4.577-6.249c-.252-.126-1.492-.737-1.723-.822-.23-.084-.398-.126-.566.126-.168.252-.651.822-.798.99-.147.168-.294.189-.546.063-.252-.126-1.065-.393-2.028-1.252-.75-.669-1.256-1.495-1.402-1.748-.147-.252-.016-.388.11-.513.112-.112.252-.294.378-.441.126-.147.168-.252.252-.42.084-.168.042-.315-.021-.441-.063-.126-.566-1.364-.775-1.868-.21-.504-.42-.42-.566-.42h-.483c-.168 0-.441.063-.672.315-.231.252-.882.861-.882 2.1 0 1.239.901 2.436 1.028 2.604.126.168 1.775 2.71 4.301 3.8.6.26 1.069.416 1.435.533.603.191 1.152.164 1.587.1.484-.073 1.492-.609 1.702-1.198.21-.589.21-1.094.147-1.198-.063-.105-.231-.168-.483-.294z"/>
                  </svg>
                  WhatsApp
                </button>
                <a 
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    color: '#94a3b8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s ease',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#E4405F'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#E4405F' }}>
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                  </svg>
                  Follow on Instagram
                </a>
                <a 
                  href={YOUTUBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    color: '#94a3b8',
                    textDecoration: 'none',
                    fontSize: '0.85rem',
                    transition: 'color 0.2s ease',
                    width: 'fit-content'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#FF0000'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#FF0000' }}>
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.376.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.376-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  Subscribe on YouTube
                </a>
              </div>
            </div>
          </div>

          <div style={{ maxWidth: '1280px', margin: '0 auto', paddingTop: '30px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}>
            <p style={{ margin: 0 }}>&copy; {new Date().getFullYear()} Shabaaz Trading Co.</p>
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <a 
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  color: '#94a3b8',
                  textDecoration: 'none',
                  fontSize: '0.75rem'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#E4405F' }}>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                </svg>
                Instagram
              </a>
              <a 
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  color: '#94a3b8',
                  textDecoration: 'none',
                  fontSize: '0.75rem'
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#FF0000' }}>
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.376.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.376-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                YouTube
              </a>
              <button onClick={handleSecondaryWhatsApp} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.75rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                💬 WhatsApp
              </button>
            </div>
          </div>
        </footer>

        {/* INQUIRY MODAL */}
        {isModalOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(9, 13, 22, 0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001, backdropFilter: 'blur(8px)' }}>
            <div style={{ backgroundColor: '#ffffff', padding: isMobile ? '24px 20px' : '36px', borderRadius: '16px', width: '92%', maxWidth: '480px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #f1f5f9' }}>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', letterSpacing: '-0.02em', color: '#0f172a' }}>Inquiry Specification</h3>
                <button onClick={closeModalAndReset} style={{ border: 'none', background: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b', fontWeight: '600' }}>✕</button>
              </div>
              
              {modalType === 'single' ? (
                <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.4' }}>Target Asset: <strong style={{ color: '#0284c7', fontWeight: '600' }}>{selectedAsset?.name}</strong></p>
              ) : (
                <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '0.9rem', lineHeight: '1.4' }}>Review your custom list of <strong style={{ color: '#16a34a', fontWeight: '600' }}>{cart.length} machinery items</strong> before submission:</p>
              )}
              
              {modalType === 'bulk' && (
                <div style={{ backgroundColor: '#f8fafc', padding: '6px 12px', borderRadius: '10px', maxHeight: '120px', overflowY: 'auto', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
                  {cart.map(item => (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ fontWeight: '600', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80%' }}>• {item.name}</span>
                      <button onClick={() => removeFromCart(item.id)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '700', fontSize: '0.8rem', padding: '4px' }}>Delete</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>Full Name *</label>
                  <input type="text" name="customerName" value={formData.customerName} onChange={handleInputChange} placeholder="John Doe" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box', outline: 'none', fontSize: '0.9rem' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>Phone Number *</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+1 (555) 000-0000" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box', outline: 'none', fontSize: '0.9rem' }} />
                </div>
                
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>Pincode *</label>
                    <input type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} placeholder="360001" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box', outline: 'none', fontSize: '0.9rem' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>City *</label>
                    <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="Rajkot" style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', boxSizing: 'border-box', outline: 'none', fontSize: '0.9rem' }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px', color: '#334155' }}>Extra Notes / Requirements</label>
                  <textarea name="notes" rows="2" value={formData.notes} onChange={handleInputChange} placeholder="Provide target timeline, customizations required etc..." style={{ width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '8px', resize: 'none', boxSizing: 'border-box', outline: 'none', fontSize: '0.9rem', lineHeight: '1.4' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '16px' }}>
                  <button onClick={handleWhatsAppSubmit} style={{ padding: '14px', backgroundColor: '#25D366', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', width: '100%', fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(37, 211, 102, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    💬 Submit via WhatsApp (Primary)
                  </button>
                  <button onClick={handleWebGmailSubmit} style={{ padding: '14px', backgroundColor: '#ea4335', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', width: '100%', fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(234, 67, 53, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    🌐 Open in Browser (Gmail Web)
                  </button>
                  <button onClick={handleNativeMailSubmit} style={{ padding: '14px', backgroundColor: '#0f172a', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', width: '100%', fontSize: '0.9rem', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    📱 Open Default Email App
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ANIMATION STYLES */}
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        
        .cart-bounce {
          animation: cartBounce 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        @keyframes cartBounce {
          0% { transform: scale(1); }
          30% { transform: scale(1.25); }
          60% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
};

// Main App Component - FIXED: Wrapped with HelmetProvider
function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;